import { useState } from "react";
import {
  X,
  Copy,
  Check,
  Compass,
  Share,
  PlusSquare,
  CheckCircle2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import styles from "./IosInstallBanner.module.css";

type IosBannerType = "notSafari" | "safari";

interface IosInstallBannerProps {
  variant: IosBannerType;
  onDismiss: () => void;
}

const SafariIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="14" fill="url(#sg)" />
    <circle
      cx="14"
      cy="14"
      r="8"
      stroke="white"
      strokeOpacity="0.4"
      strokeWidth="0.8"
      fill="none"
    />
    <polygon points="14,7 15.2,13 14,14 12.8,13" fill="#FF3B30" />
    <polygon points="14,21 12.8,15 14,14 15.2,15" fill="white" />
    <defs>
      <linearGradient id="sg" x1="0" y1="0" x2="28" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#34AADC" />
        <stop offset="100%" stopColor="#007AFF" />
      </linearGradient>
    </defs>
  </svg>
);

const IosInstallBanner: React.FC<IosInstallBannerProps> = ({ variant, onDismiss }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  if (variant === "notSafari") {
    return (
      <div className={`${styles.banner} ${styles.bannerWarning}`}>
        <div className={styles.header}>
          <Compass className={styles.iconWarning} aria-hidden="true" />
          <div className={styles.text}>
            <p className={styles.title}>{t("home.ios.notSafariTitle")}</p>
            <p className={styles.description}>{t("home.ios.notSafariMessage")}</p>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className={styles.dismiss}
            aria-label={t("home.closeBanner")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            onClick={() => {
              window.location.href = `x-safari-${window.location.href}`;
            }}
            className={styles.btnPrimary}
          >
            <Compass className="w-3.5 h-3.5" aria-hidden="true" />
            {t("home.ios.openSafari")}
          </button>
          <button type="button" onClick={handleCopy} className={styles.btnSecondary}>
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5" aria-hidden="true" />
                {t("home.ios.copied")}
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                {t("home.ios.copyUrl")}
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.banner} ${styles.bannerInfo}`}>
      <div className={styles.header}>
        <SafariIcon />
        <div className={styles.text}>
          <p className={styles.title}>{t("home.ios.safariTitle")}</p>
          <p className={styles.description}>{t("home.ios.safariMessage")}</p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className={styles.dismissInfo}
          aria-label={t("home.closeBanner")}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className={styles.steps}>
        <div className={styles.step}>
          <Share className={styles.stepIcon} aria-hidden="true" />
          <div>
            <p className={styles.stepTitle}>{t("home.ios.step1Title")}</p>
            <p className={styles.stepDesc}>{t("home.ios.step1Desc")}</p>
          </div>
        </div>
        <div className={styles.step}>
          <PlusSquare className={styles.stepIcon} aria-hidden="true" />
          <div>
            <p className={styles.stepTitle}>{t("home.ios.step2Title")}</p>
            <p className={styles.stepDesc}>{t("home.ios.step2Desc")}</p>
          </div>
        </div>
        <div className={styles.step}>
          <CheckCircle2 className={styles.stepIcon} aria-hidden="true" />
          <div>
            <p className={styles.stepTitle}>{t("home.ios.step3Title")}</p>
            <p className={styles.stepDesc}>{t("home.ios.step3Desc")}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IosInstallBanner;
